import React, { useState, useEffect } from 'react';
import { Save, Palette, Mail, CheckCircle, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { getClientBranding, updateClientBranding, verifyBrandingEmail, checkEmailVerificationStatus, ClientBranding } from '@/actions/drip-campaigns';

const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'linkedin', 'instagram', 'youtube'];

const ClientBrandingPage: React.FC = () => {
  const [branding, setBranding] = useState<Partial<ClientBranding>>({
    logo_url: '',
    logo_width: 200,
    logo_height: 60,
    primary_color: '#0066cc',
    secondary_color: '#333333',
    accent_color: '#ff6600',
    background_color: '#f5f5f5',
    company_name: '',
    company_address: '',
    support_email: '',
    support_phone: '',
    website_url: '',
    social_links: {},
    footer_text: '',
    show_unsubscribe_link: true,
    unsubscribe_text: 'Unsubscribe from this list',
    show_address: true,
    show_social_links: true,
    custom_disclaimer: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    verified: boolean;
    status: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadBranding();
  }, []);

  useEffect(() => {
    if (branding.from_email && !loading) {
      checkVerificationStatus();
    }
  }, [branding.from_email, loading]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const response = await getClientBranding();
      if (response.success && response.data) {
        setBranding(response.data);
      }
    } catch (error: any) {
      // If no branding exists yet, that's okay - we'll create it on save
      if (error.response?.status !== 404) {
        console.error('Error loading branding:', error);
        toast.error('Failed to load branding settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ClientBranding, value: any) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSocialLinkChange = (platform: string, url: string) => {
    setBranding((prev) => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: url,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateClientBranding(branding);
      toast.success('Branding settings saved successfully');
      setHasChanges(false);
      loadBranding();
    } catch (error: any) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.error || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!branding.from_email) {
      toast.error('Please enter a from email address first');
      return;
    }

    // Save first if there are changes
    if (hasChanges) {
      try {
        await handleSave();
      } catch (error) {
        toast.error('Please save your changes first');
        return;
      }
    }

    try {
      setVerifying(true);
      const response = await verifyBrandingEmail();
      if (response.success) {
        toast.success(response.message);
        setTimeout(() => checkVerificationStatus(), 2000);
      }
    } catch (error: any) {
      console.error('Error verifying email:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate email verification');
    } finally {
      setVerifying(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!branding.from_email) {
      setVerificationStatus(null);
      return;
    }

    try {
      setCheckingStatus(true);
      const response = await checkEmailVerificationStatus();
      if (response.success) {
        setVerificationStatus({
          verified: (response as any).verified || false,
          status: (response as any).status || 'Unknown',
          message: (response as any).message || 'Status unavailable',
        });
      }
    } catch (error: any) {
      console.error('Error checking verification status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Email Branding"
        description="Customize how your emails look with your brand colors, logo, and company information"
        icon={<Palette className="h-5 w-5" />}
      />

      <div className="p-6">
      {/* Save Button - Fixed at top */}
      {hasChanges && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            You have unsaved changes
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Logo Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Logo
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={branding.logo_url || ''}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    value={branding.logo_width || 200}
                    onChange={(e) => handleInputChange('logo_width', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    value={branding.logo_height || 60}
                    onChange={(e) => handleInputChange('logo_height', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {branding.logo_url && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                  <img
                    src={branding.logo_url}
                    alt="Logo preview"
                    style={{
                      width: `${branding.logo_width}px`,
                      height: `${branding.logo_height}px`,
                    }}
                    className="object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Colors Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Brand Colors
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.primary_color || '#0066cc'}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.primary_color || '#0066cc'}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.secondary_color || '#333333'}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.secondary_color || '#333333'}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Accent Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.accent_color || '#ff6600'}
                    onChange={(e) => handleInputChange('accent_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.accent_color || '#ff6600'}
                    onChange={(e) => handleInputChange('accent_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Background Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.background_color || '#f5f5f5'}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.background_color || '#f5f5f5'}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Company Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={branding.company_name || ''}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Address
                </label>
                <textarea
                  value={branding.company_address || ''}
                  onChange={(e) => handleInputChange('company_address', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={branding.support_email || ''}
                    onChange={(e) => handleInputChange('support_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="support@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={branding.support_phone || ''}
                    onChange={(e) => handleInputChange('support_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={branding.website_url || ''}
                  onChange={(e) => handleInputChange('website_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* From Address Configuration */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  From Address
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Configure the email address that drip campaigns will be sent from
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    From Email Address *
                  </label>
                  <input
                    type="email"
                    value={branding.from_email || ''}
                    onChange={(e) => handleInputChange('from_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="noreply@yourcompany.com"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This email must be verified with AWS SES
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    From Name
                  </label>
                  <input
                    type="text"
                    value={branding.from_name || ''}
                    onChange={(e) => handleInputChange('from_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your Company"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Display name shown in emails
                  </p>
                </div>
              </div>

              {/* Verification Status */}
              {branding.from_email && (
                <div className="mt-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {verificationStatus?.verified ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-400">
                            Email Verified
                          </span>
                        </>
                      ) : verificationStatus?.status === 'Pending' ? (
                        <>
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                            Verification Pending
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="text-sm font-medium text-red-700 dark:text-red-400">
                            Not Verified
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={checkVerificationStatus}
                        disabled={checkingStatus}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${checkingStatus ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      {!verificationStatus?.verified && (
                        <button
                          onClick={handleVerifyEmail}
                          disabled={verifying || !branding.from_email}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-brandColor text-white rounded hover:bg-brand-hover disabled:opacity-50"
                        >
                          <Mail className="w-4 h-4" />
                          {verifying ? 'Sending...' : 'Verify Email'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    {verificationStatus?.message || 'Click "Verify Email" to receive a verification link'}
                  </p>
                </div>
              )}

              {/* Information Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                  How Email Verification Works
                </h4>
                <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Enter your from email address and save settings</li>
                  <li>Click "Verify Email" to receive a verification email from AWS SES</li>
                  <li>Check your inbox and click the verification link</li>
                  <li>Come back and click "Refresh" to update the status</li>
                  <li>Once verified, all drip campaigns will use this email address</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Social Media Links
            </h2>
            <div className="space-y-4">
              {SOCIAL_PLATFORMS.map((platform) => (
                <div key={platform}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                    {platform}
                  </label>
                  <input
                    type="url"
                    value={branding.social_links?.[platform] || ''}
                    onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`https://${platform}.com/yourcompany`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Email Footer
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Footer Text
                </label>
                <textarea
                  value={branding.footer_text || ''}
                  onChange={(e) => handleInputChange('footer_text', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Thank you for choosing our services..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Disclaimer
                </label>
                <textarea
                  value={branding.custom_disclaimer || ''}
                  onChange={(e) => handleInputChange('custom_disclaimer', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Legal disclaimer or privacy notice..."
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_address || false}
                    onChange={(e) => handleInputChange('show_address', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show company address in footer
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_social_links || false}
                    onChange={(e) => handleInputChange('show_social_links', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show social media links in footer
                  </span>
                </label>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_unsubscribe_link || false}
                    onChange={(e) => handleInputChange('show_unsubscribe_link', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show unsubscribe link
                  </span>
                </label>
                {branding.show_unsubscribe_link && (
                  <input
                    type="text"
                    value={branding.unsubscribe_text || ''}
                    onChange={(e) => handleInputChange('unsubscribe_text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Unsubscribe text"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h2>
            <div
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              style={{ backgroundColor: branding.background_color }}
            >
              {/* Logo */}
              {branding.logo_url && (
                <div className="mb-4 text-center">
                  <img
                    src={branding.logo_url}
                    alt="Logo"
                    style={{
                      width: `${(branding.logo_width || 200) / 2}px`,
                      height: `${(branding.logo_height || 60) / 2}px`,
                    }}
                    className="mx-auto object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Content Sample */}
              <div className="bg-white p-3 rounded mb-4">
                <h3
                  className="text-sm font-bold mb-2"
                  style={{ color: branding.primary_color }}
                >
                  Sample Heading
                </h3>
                <p className="text-xs mb-2" style={{ color: branding.secondary_color }}>
                  This is how your email content will look with your brand colors.
                </p>
                <button
                  className="text-xs px-3 py-1 rounded text-white"
                  style={{ backgroundColor: branding.accent_color }}
                >
                  Call to Action
                </button>
              </div>

              {/* Footer Sample */}
              <div className="text-center pt-3 border-t border-gray-200">
                {branding.footer_text && (
                  <p className="text-xs text-gray-600 mb-2">{branding.footer_text}</p>
                )}
                {branding.company_name && (
                  <p className="text-xs font-semibold text-gray-800 mb-1">
                    {branding.company_name}
                  </p>
                )}
                {branding.show_address && branding.company_address && (
                  <p className="text-xs text-gray-600 mb-2">{branding.company_address}</p>
                )}

                {/* Contact Information */}
                <div className="text-xs text-gray-600 mb-2 space-y-1">
                  {branding.support_email && (
                    <p>
                      Email: <a href={`mailto:${branding.support_email}`} className="text-blue-600 hover:underline">{branding.support_email}</a>
                    </p>
                  )}
                  {branding.support_phone && (
                    <p>
                      Phone: <a href={`tel:${branding.support_phone}`} className="text-blue-600 hover:underline">{branding.support_phone}</a>
                    </p>
                  )}
                  {branding.website_url && (
                    <p>
                      <a href={branding.website_url} target="_blank" rel="noopener noreferrer" className="text-brandColor hover:underline">
                        Visit our website
                      </a>
                    </p>
                  )}
                </div>

                {/* Social Media Links */}
                {branding.show_social_links && branding.social_links && Object.keys(branding.social_links).some(platform => branding.social_links?.[platform]) && (
                  <div className="flex justify-center gap-3 mb-2">
                    {SOCIAL_PLATFORMS.map((platform) => {
                      const url = branding.social_links?.[platform];
                      if (!url) return null;
                      const socialIcons: Record<string, React.ReactElement> = {
                        facebook: (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                            <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                          </svg>
                        ),
                        twitter: (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#000000">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        ),
                        linkedin: (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        ),
                        instagram: (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#ig-gradient)">
                            <defs>
                              <linearGradient id="ig-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#FED373"/>
                                <stop offset="25%" stopColor="#F15245"/>
                                <stop offset="50%" stopColor="#D92E7F"/>
                                <stop offset="75%" stopColor="#9B36B7"/>
                                <stop offset="100%" stopColor="#515ECF"/>
                              </linearGradient>
                            </defs>
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                          </svg>
                        ),
                        youtube: (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FF0000">
                            <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        ),
                      };
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-80 transition-opacity"
                          title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                        >
                          {socialIcons[platform] ?? (
                            <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-xs font-bold uppercase">{platform[0]}</span>
                            </div>
                          )}
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Custom Disclaimer */}
                {branding.custom_disclaimer && (
                  <p className="text-xs text-gray-500 mb-2 italic">
                    {branding.custom_disclaimer}
                  </p>
                )}

                {branding.show_unsubscribe_link && (
                  <p className="text-xs">
                    <a href="#" className="text-brandColor underline">
                      {branding.unsubscribe_text || 'Unsubscribe'}
                    </a>
                  </p>
                )}
              </div>
            </div>

            {/* Save Button - Inside Preview Sidebar */}
            <div className="mt-4">
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Branding Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ClientBrandingPage;
